import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Mutation, MutationBlockNfcCardArgs, NfcCard, Query } from 'graphql/generated/graphqlTypes';
import { BLOCK_ACCESS_CARD } from 'graphql/v2/mutations';
import {
  GET_ACCESS_CARDS,
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect } from 'react';
import { TranslatedHookProps } from '../hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> =
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_ACCESS_CARDS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> = GET_ACCESS_CARDS;
const BLOCK_ACCESS_CARD_TYPED: TypedDocumentNode<
  Pick<Mutation, 'blockNfcCard'>,
  MutationBlockNfcCardArgs
> = BLOCK_ACCESS_CARD;

const useAccessCards = ({ translation, handleFetchError }: TranslatedHookProps) => {
  // The cards only hold the id of the visitor they are assigned to, so the visitors are fetched next to them.
  const {
    loading: cardsLoading,
    error: cardsError,
    data: cardsData
  } = useQuery(GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (cardsError) {
      handleFetchError(translation.access_card_error_fetching);
      console.error(cardsError);
    }
  }, [cardsError]);

  const {
    loading: visitorsLoading,
    error: visitorsError,
    data: visitorsData
  } = useQuery(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  // A failed visitors request only costs the visitor details on the cards (email, expiry): the cards stay visible.
  useEffect(() => {
    if (visitorsError) {
      handleFetchError(translation.users_error_fetching);
      console.error(visitorsError);
    }
  }, [visitorsError]);

  // Only the very first fetch counts as pending: with cached data the cards stay on screen while both queries refresh.
  const pendingCards = (cardsLoading && !cardsData) || (visitorsLoading && !visitorsData);

  // True only while a request is in flight over cached data. `loading` is used instead of the network status because
  // a failed refetch leaves the status on `error`, which would keep the "refreshing" indicator on until the next success.
  const isRefetchingAccessCards =
    !pendingCards && ((!!cardsData && cardsLoading) || (!!visitorsData && visitorsLoading));

  useRegisterBackgroundRefetch(isRefetchingAccessCards);

  // Read the cards together with their visitor straight from the normalized cache once both queries have settled.
  // A visitor that is missing from the cache stays a `{ id }` object (never null/undefined), so the card is not
  // reported as belonging to a deleted user.
  const {
    loading: composedLoading,
    data: accessCardsData,
    error: composedError
  } = useQuery(GET_ACCESS_CARDS_TYPED, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: pendingCards
  });

  useEffect(() => {
    if (composedError) {
      handleFetchError(translation.access_card_error_fetching);
      console.error(composedError);
    }
  }, [composedError]);

  const [blockAccessCardMutation, { loading: blockingAccessCard, error: blockAccessCardError }] = useMutation(
    BLOCK_ACCESS_CARD_TYPED,
    {
      refetchQueries: [{ query: GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS }],
      awaitRefetchQueries: true
    }
  );

  useEffect(() => {
    if (blockAccessCardError) {
      handleFetchError(translation.access_card_error_blocking);
      console.error(blockAccessCardError);
    }
  }, [blockAccessCardError]);

  const blockAccessCard = async (uuid: string, reason: string) => {
    await blockAccessCardMutation({ variables: { uuid, reason } });
  };

  return {
    // Apollo 4 types results with `returnPartialData: true` as DeepPartial, because the visitor details may be
    // missing from the cache. They are cast to the GraphQL type to preserve the existing hook API.
    accessCards: (accessCardsData?.getNfcCards as NfcCard[] | undefined) || [],
    loadingAccessCards: pendingCards || composedLoading,
    isRefetchingAccessCards,
    blockAccessCard,
    blockingAccessCard
  };
};

export default useAccessCards;
